import { Functions } from 'firebase/functions';
import { RemoteConfig } from 'firebase/remote-config';
import { CancelSubscriptionInteractor } from './domain/interactors/cancel-subscription.interactor';
import { ChangeSubscriptionInteractor } from './domain/interactors/change-subscription.interactor';
import { CreateSubscriptionInteractor } from './domain/interactors/create-subscription.interactor';
import { GetCustomerPortalUrlInteractor } from './domain/interactors/get-customer-portal-url.interactor';
import { GetPaymentResultInteractor } from './domain/interactors/get-payment-result.interactor';
import { GetPaywallDetailsInteractor } from './domain/interactors/get-paywall-details.interactor';
import { GetUpgradeProrationInteractor } from './domain/interactors/get-upgrade-proration.interactor';
import { GetPaymentMetadataInteractor } from './domain/interactors/get-payment-metadata.interactor';
import { SavePaymentMetadataInteractor } from './domain/interactors/save-payment-metadata.interactor';
import { WaitPurchaseWebhookInteractor } from './domain/interactors/wait-purchase-webhook.interactor';
import { Firestore } from 'firebase/firestore';
import { TeamProvider } from '../team/team.provider';
import { AuthProvider } from '../auth/auth.provider';
import { Cached, TeamCommonDefaultProvider } from '@uploadcam/common';
import { SyncTeamSubscriptionInteractor } from './domain/interactors/sync-team-subscription.interactor';
import { Observable, Subject } from 'rxjs';

export abstract class PurchasesProvider {
  abstract provideCancelSubscription(): CancelSubscriptionInteractor;
  abstract provideChangeSubscription(): ChangeSubscriptionInteractor;
  abstract provideCreateSubscription(): CreateSubscriptionInteractor;
  abstract provideGetCustomerPortalUrl(): GetCustomerPortalUrlInteractor;
  abstract provideGetPaymentMetadata(): GetPaymentMetadataInteractor;
  abstract provideGetPaymentResult(): GetPaymentResultInteractor;
  abstract provideGetPaywallDetails(): GetPaywallDetailsInteractor;
  abstract provideGetUpgradeProration(): GetUpgradeProrationInteractor;
  abstract providePurchaseDone$(): Observable<void>;
  abstract provideSavePaymentMetadata(): SavePaymentMetadataInteractor;
  abstract provideSyncTeamSubscription(): SyncTeamSubscriptionInteractor;
  abstract provideWaitPurchaseWebhook(): WaitPurchaseWebhookInteractor;
}

export class PurchasesDefaultProvider implements PurchasesProvider {
  private readonly teamCommon = new TeamCommonDefaultProvider();
  private readonly purchaseDone$ = new Subject<void>();

  constructor(
    private readonly authProvider: AuthProvider,
    private readonly db: Firestore,
    private readonly functions: Functions,
    private readonly remoteConfig: RemoteConfig,
    private readonly teamProvider: TeamProvider,
  ) {}

  @Cached()
  public provideCancelSubscription(): CancelSubscriptionInteractor {
    return new CancelSubscriptionInteractor(this.functions);
  }

  @Cached()
  public provideChangeSubscription(): ChangeSubscriptionInteractor {
    return new ChangeSubscriptionInteractor(this.functions);
  }

  @Cached()
  public provideCreateSubscription(): CreateSubscriptionInteractor {
    return new CreateSubscriptionInteractor(this.functions);
  }

  @Cached()
  public provideGetCustomerPortalUrl(): GetCustomerPortalUrlInteractor {
    return new GetCustomerPortalUrlInteractor(this.functions);
  }

  @Cached()
  public provideGetPaymentMetadata(): GetPaymentMetadataInteractor {
    return new GetPaymentMetadataInteractor(window.localStorage);
  }

  @Cached()
  public provideGetPaymentResult(): GetPaymentResultInteractor {
    return new GetPaymentResultInteractor();
  }

  @Cached()
  public provideGetPaywallDetails(): GetPaywallDetailsInteractor {
    return new GetPaywallDetailsInteractor(this.functions, this.remoteConfig);
  }

  @Cached()
  public provideGetUpgradeProration(): GetUpgradeProrationInteractor {
    return new GetUpgradeProrationInteractor(this.functions);
  }

  @Cached()
  public providePurchaseDone$(): Observable<void> {
    return this.purchaseDone$.asObservable();
  }

  @Cached()
  public provideSavePaymentMetadata(): SavePaymentMetadataInteractor {
    return new SavePaymentMetadataInteractor(window.localStorage);
  }

  @Cached()
  public provideSyncTeamSubscription(): SyncTeamSubscriptionInteractor {
    return new SyncTeamSubscriptionInteractor(this.functions);
  }

  @Cached()
  public provideWaitPurchaseWebhook(): WaitPurchaseWebhookInteractor {
    return new WaitPurchaseWebhookInteractor(
      this.db,
      this.teamProvider.provideGetTeamById(),
      this.authProvider.provideGetUser(),
      this.teamCommon.provideTeamEntityToTeamMapper(),
      this.purchaseDone$,
      this.provideSyncTeamSubscription(),
    );
  }
}
